import { useMemo } from 'react';

import {
  MasonryGalleryDouble,
  MasonryGalleryQuad,
  MasonryGallerySingle,
  MasonryGalleryTriple,
  type MasonryGalleryImage,
} from '@/components/Work/Masonry';
import { caseStudies } from '@/data/caseStudies';
import type { CaseStudy } from '@/data/caseStudies/types';
import type { WorkGalleryLayout, WorkGalleryImage, WorkProject } from '../types';

type GalleryVideoSource = { src: string; type?: string };

export type NormalizedGallery = {
  layout: WorkGalleryLayout;
  images: MasonryGalleryImage[];
};

export type GalleryComponentsMap = Record<WorkGalleryLayout, typeof MasonryGallerySingle>;

export type DetailRow =
  | {
      key: 'task';
      label?: string;
      type: 'list';
      items: string[];
      emphasize?: boolean;
    }
  | {
      key: 'task' | 'solution' | 'result';
      label?: string;
      type: 'paragraphs';
      items: string[];
      emphasize?: boolean;
    }
  | {
      key: 'testimonial';
      label?: string;
      type: 'testimonial';
      quote: string;
      author?: string;
      role?: string;
      avatarUrl?: string;
    }
  | {
      key: 'stack';
      label?: string;
      type: 'chips';
      items: string[];
    };

export const GALLERY_COMPONENT_MAP: GalleryComponentsMap = {
  single: MasonryGallerySingle,
  double: MasonryGalleryDouble,
  triple: MasonryGalleryTriple,
  quad: MasonryGalleryQuad,
};

const asArray = (value: string | string[] | undefined): string[] => {
  if (!value) return [];
  return Array.isArray(value) ? value : [value];
};

const buildNormalizedVideoSources = (sources: GalleryVideoSource[]): GalleryVideoSource[] =>
  sources
    .map(({ src, type }) => {
      const generated: GalleryVideoSource[] = [];
      const extension = src.split('.').pop()?.toLowerCase();
      const inferredType =
        type ??
        (extension === 'mp4'
          ? 'video/mp4'
          : extension === 'mov'
            ? 'video/quicktime'
            : undefined);

      if (inferredType) {
        generated.push({ src, type: inferredType });
      } else {
        generated.push({ src });
      }

      if (inferredType === 'video/quicktime' || extension === 'mov') {
        generated.push({ src, type: 'video/mp4' });
      }

      return generated;
    })
    .flat()
    .filter(
      (value, index, array) =>
        array.findIndex((other) => other.src === value.src && other.type === value.type) === index,
    );

export const useExpandedViewContent = (project: WorkProject) => {
  const caseStudy = useMemo<CaseStudy | undefined>(() => {
    const identifiers = [project.caseStudySlug, project.id].filter(Boolean) as string[];
    if (identifiers.length === 0) {
      return undefined;
    }

    return caseStudies.find((entry) =>
      identifiers.some(
        (identifier) => entry.slug === identifier || entry.id === identifier,
      ),
    );
  }, [project.caseStudySlug, project.id]);

  const defaultDetails = useMemo(
    () => ({
      tasks: [
        `Nortec was established to provide their customers with the best solutions on the market within laundry facilities for housing associations, property owner associations, cleaning companies, and more. Nortec offers complete solutions, so the customer doesn't have to worry about anything—they handle everything from installation, maintenance, to customer service. Nortec was established in 1988 with servicing laundries as its core competence and has recently introduced a solution focusing on charging stations.`,
        `Therefore, Nortec approached Dwarf with the task of creating a new website with an updated digital visual identity and a design that could easily scale with their business.`,
      ],
      solutions: [
        `Based on a utilitarian design language, Nortec's new digital identity is simple, bold, and colorful. Throughout the website, color themes and icons are used both as wayfinding and branding elements, creating a clear visual experience for key business areas while helping users navigate different sections of the site. Nortec is a detail-oriented business that strives to provide the best possible service and solutions to its customers. Therefore, it was important for customers to feel guided through their user journey on the website.`,
        `The solution is a flexible website with a CMS in Sanity, integrated with Zendesk for opening hours and customer service. Integration with SendGrid allows emails to be sent out seamlessly. With Sanity, Nortec has obtained a CMS that makes it easy to create and manage content according to their needs. It also provides great creative freedom for editors to customize developed modules with desired colors, icons, and other design elements as needed.`,
      ],
      results: [
        `With the new website, Nortec has gained significant editorial freedom in the CMS, making it easier for them to create content and scale to new markets. The hope is that the website will increasingly serve as a strong sales channel for Nortec. The website went live on June 11, 2024 and in August 2024 the english version of the website went live.`,
      ],
      testimonial: {
        quote: `Skilled, curious, and dedicated. These words not only describe our own core values but also summarize the collaboration we've had with Dwarf. They have dedicated themselves to our business from the start, assembled a team that matched who we are, and have done what they do best: designed and developed their way into our hearts. The journey to the finished website hasn't been without its challenges—but along the way, we have collectively managed to discuss the details and prioritize our wishes, getting as close to our ambition as possible. An exercise that underscores the strength of choosing Dwarf and clearly reflects in the final result. All in all, an agency that has delivered everything we desired.`,
        author: 'Viktor Sand',
        role: 'Marketing Manager',
      },
      stack: ['Sanity', 'Next.js', 'ReactJS'],
    }),
    [],
  );

  const detailSections = project.details ?? defaultDetails;
  const testimonial =
    detailSections.testimonial ?? (project.quote ? { quote: project.quote, author: '', role: '' } : undefined);
  const stackItems = Array.isArray(detailSections.stack) && detailSections.stack.length > 0 ? detailSections.stack : project.tags;

  const detailRows = useMemo<DetailRow[]>(() => {
    if (caseStudy?.caseDetails) {
      const rows: DetailRow[] = [];
      const { task, solution, result, testimonial: csTestimonial, stack } = caseStudy.caseDetails;

      if (task) {
        rows.push({ key: 'task', label: 'Task', type: 'paragraphs', items: [task] });
      }

      if (solution) {
        rows.push({ key: 'solution', label: 'Solution', type: 'paragraphs', items: [solution] });
      }

      if (result) {
        rows.push({ key: 'result', label: 'Result', type: 'paragraphs', items: [result] });
      }

      const testimonialRow = csTestimonial ?? testimonial;
      if (testimonialRow) {
        rows.push({
          key: 'testimonial',
          label: 'Testimonial',
          type: 'testimonial',
          quote: testimonialRow.quote,
          author: testimonialRow.author,
          role: testimonialRow.role,
          avatarUrl: 'avatarUrl' in testimonialRow ? (testimonialRow as { avatarUrl?: string }).avatarUrl : undefined,
        } as DetailRow);
      }

      const stackRow = stack && stack.length > 0 ? stack : stackItems;
      if (stackRow && stackRow.length > 0) {
        rows.push({ key: 'stack', label: 'Stack', type: 'chips', items: stackRow });
      }

      return rows;
    }

    const rows: DetailRow[] = [];

    const tasks = asArray(detailSections.tasks);
    if (tasks.length > 0) {
      rows.push({
        key: 'task',
        label: 'Problem',
        type: 'list',
        items: tasks,
        emphasize: true,
      });
    }

    const solutions = asArray(detailSections.solutions);
    if (solutions.length > 0) {
      rows.push({ key: 'solution', label: 'Solution', type: 'paragraphs', items: solutions });
    }

    const results = asArray(detailSections.results);
    if (results.length > 0) {
      rows.push({ key: 'result', label: 'Result', type: 'paragraphs', items: results });
    }

    if (testimonial) {
      rows.push({
        key: 'testimonial',
        label: 'Testimonial',
        type: 'testimonial',
        quote: testimonial.quote,
        author: testimonial.author,
        role: testimonial.role,
      });
    }

    if (stackItems && stackItems.length > 0) {
      rows.push({ key: 'stack', label: 'Stack', type: 'chips', items: stackItems });
    }

    return rows;
  }, [caseStudy?.caseDetails, detailSections.results, detailSections.solutions, detailSections.tasks, stackItems, testimonial]);

  const hasDetails = detailRows.length > 0;

  const videoSources =
    project.mediaSources && project.mediaSources.length > 0
      ? project.mediaSources
      : [
          {
            src: project.media,
            type: project.media.endsWith('.mov') ? 'video/quicktime' : 'video/mp4',
          },
        ];

  const normalizedVideoSources = useMemo(
    () => buildNormalizedVideoSources(videoSources),
    [videoSources],
  );

  const primaryVideoSource = normalizedVideoSources[0] ?? videoSources[0];

  const heroGalleryMedia: MasonryGalleryImage = (() => {
    const imageSource = caseStudy?.heroGalleryImage ?? caseStudy?.heroImageUrl;

    if (imageSource) {
      const isVideo =
        typeof imageSource === 'string' &&
        (imageSource.toLowerCase().endsWith('.mp4') ||
          imageSource.toLowerCase().endsWith('.webm') ||
          imageSource.toLowerCase().endsWith('.mov'));

      if (isVideo) {
        return {
          src: imageSource,
          alt: `${project.title} showcase`,
          kind: 'video',
          autoPlay: true,
          loop: true,
          muted: true,
          playsInline: true,
        } satisfies MasonryGalleryImage;
      }

      return {
        src: imageSource,
        alt: `${project.title} showcase`,
      } satisfies MasonryGalleryImage;
    }

    if (project.mediaType === 'video') {
      return {
        src: primaryVideoSource?.src ?? project.media,
        alt: `${project.title} showcase`,
        kind: 'video',
        sources: normalizedVideoSources,
        autoPlay: true,
        loop: true,
        muted: true,
        playsInline: true,
      } satisfies MasonryGalleryImage;
    }

    return {
      src: project.media,
      alt: `${project.title} showcase`,
    } satisfies MasonryGalleryImage;
  })();

  const gallerySets = useMemo<NormalizedGallery[]>(() => {
    if (!project.galleries || project.galleries.length === 0) {
      return [];
    }

    return project.galleries
      .map<NormalizedGallery>((gallery) => ({
        layout: gallery.layout,
        images: gallery.images.map((image, index) => ({
          ...image,
          alt: image.alt || `${project.title} gallery ${index + 1}`,
          caption: image.caption ?? image.alt,
        })),
      }))
      .filter((gallery) => gallery.images.length > 0);
  }, [project.galleries, project.title]);

  return {
    detailRows,
    hasDetails,
    heroGalleryMedia,
    gallerySets,
    videoSources: normalizedVideoSources,
    caseStudy,
  };
};
